import { useMemo, useState } from 'react';
import { ShieldAlert } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Button, Chip, Skeleton } from '@vnetwork/ui/primitives';
import {
  DataTable,
  EmptyState,
  Pagination,
  StatusPill,
  type ColumnDef,
  type StatusTone,
} from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { TableLink } from '@/components/table-link';
import { formatDateTime } from '@/lib/format';
import { paginationView } from '@/pages/customers/pagination-summary';
import { useWafReports, type WafReport } from '@/api/waf-reports';

const PAGE_SIZE = 20;

/** Map report_status → StatusPill tone. */
function reportStatusTone(s?: string): StatusTone {
  const v = (s ?? '').toLowerCase();
  if (v === 'active') return 'success';
  if (['paused', 'inactive', 'disabled'].includes(v)) return 'neutral';
  if (['error', 'failed'].includes(v)) return 'danger';
  return 'neutral';
}

/** Humanize a lowercase token (e.g. "weekly" → "Weekly"). */
function humanize(s?: string): string {
  if (!s) return '—';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const COLUMNS: ColumnDef<WafReport, unknown>[] = [
  {
    id: 'report_name',
    header: 'Name',
    cell: ({ row }) => (
      <span style={{ fontWeight: 'var(--vn-font-weight-medium)', overflowWrap: 'anywhere' }}>
        {row.original.report_name || `#${row.original.report_id}`}
      </span>
    ),
  },
  {
    id: 'service_id',
    header: 'Service',
    cell: ({ row }) =>
      row.original.service_id ? (
        <TableLink to={`/services/${row.original.service_id}`}>{row.original.service_id}</TableLink>
      ) : (
        <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>
      ),
  },
  {
    id: 'report_interval',
    header: 'Interval',
    cell: ({ row }) => <Chip tone="neutral">{humanize(row.original.report_interval)}</Chip>,
  },
  {
    id: 'report_scope',
    header: 'Scope',
    cell: ({ row }) => (
      <span style={{ color: 'var(--vn-fg-muted)' }}>{humanize(row.original.report_scope)}</span>
    ),
  },
  {
    id: 'provider',
    header: 'Provider',
    cell: ({ row }) => (
      <span style={{ color: 'var(--vn-fg-muted)' }}>{row.original.provider || '—'}</span>
    ),
  },
  {
    id: 'report_priority',
    header: () => <div style={{ textAlign: 'right' }}>Priority</div>,
    cell: ({ row }) => <div style={{ textAlign: 'right' }}>{row.original.report_priority ?? '—'}</div>,
  },
  {
    id: 'report_status',
    header: 'Status',
    cell: ({ row }) => (
      <StatusPill tone={reportStatusTone(row.original.report_status)}>
        {humanize(row.original.report_status)}
      </StatusPill>
    ),
  },
  {
    id: 'last_send_date',
    header: () => <div style={{ textAlign: 'right' }}>Last Sent</div>,
    cell: ({ row }) => (
      <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        {row.original.last_send_date ? formatDateTime(row.original.last_send_date) : '—'}
      </div>
    ),
  },
];

/**
 * WAF Reports — the Technical › Report list. Scheduled WAF report definitions
 * served by the automation service (`GET /workers/reports`). Read-only list for
 * now; resend / create / edit / pipeline / history are follow-ups.
 */
export default function TechnicalReportPage() {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, isFetching, refetch } = useWafReports({ page, page_size: PAGE_SIZE });

  const rows = data?.reports ?? [];
  const total = data?.pagination?.total ?? rows.length;
  const pageCount = data?.pagination?.total_pages ?? 1;

  const footer = useMemo(() => {
    const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);
    return (
      <Pagination
        page={page}
        pageCount={view.pageCount}
        onPageChange={setPage}
        summary={`${view.summary}${isFetching ? ' …' : ''}`}
      />
    );
  }, [page, rows.length, total, pageCount, isFetching]);

  return (
    <Container size="full">
      <PageHeader
        title="WAF Reports"
        description="Scheduled Web Application Firewall reports and their send status."
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        {isError ? (
          <Alert
            tone="danger"
            title="Failed to load WAF reports"
            actions={
              <Button size="sm" variant="ghost" onClick={() => refetch()}>
                Retry
              </Button>
            }
          />
        ) : isLoading ? (
          <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 40 }} />
            ))}
          </div>
        ) : (
          <DataTable
            columns={COLUMNS}
            data={rows}
            getRowId={(r) => r.id ?? String(r.report_id)}
            emptyState={
              <EmptyState
                icon={<ShieldAlert size={24} />}
                title="No WAF reports"
                description="No scheduled WAF reports are configured yet."
              />
            }
            footer={footer}
          />
        )}
      </div>
    </Container>
  );
}
